/**
 * The module's data source: the backend's Internal API under /api/events (ARCHITECTURE.md), which proxies the
 * catalog with the private key. No browser ever calls app.kgob.com and no key is held here (spec §11.5).
 *
 *  GET /api/events/featured       {items, fallback, generated_at, stale}   the hero, on a 5-minute timer
 *  GET /api/events/event/{slug}   Event · {redirect_to} · {gone:true} · 404 the share view, cached 5 minutes per slug
 *
 * Last-good: a failed refresh keeps the previous answer; the hero is never blanked because a call failed.
 * First paint: when the postbuild script baked window.__EVENTS_FEATURED__ into the HTML, the store starts
 * from that snapshot so the hero renders at once and refreshes in the background.
 */
import { useEffect, useState } from "react";
import { isSlug } from "./gate";

export const API_BASE = "/api/events";
export const REFRESH_MS = 5 * 60 * 1000;

const snapshotFromWindow = () => {
  if (typeof window === "undefined") return null;
  const s = window.__EVENTS_FEATURED__;
  return s && Array.isArray(s.items) ? s : null;
};

// Module-level last-good copies (one per page life).
let lastGoodFeatured = snapshotFromWindow();
const slugCache = new Map();
const listeners = new Set();

export const getFeatured = () => lastGoodFeatured;

function setFeatured(list) {
  lastGoodFeatured = list;
  (listeners || []).forEach((fn) => fn(list));
}

async function getJson(url, fetcher) {
  const res = await fetcher(url, { headers: { Accept: "application/json" }, credentials: "same-origin" });
  return { status: res.status, ok: res.ok, body: res.ok || res.status === 404 ? await res.json().catch(() => null) : null };
}

/** Refreshes the featured list. Resolves to the current best copy (new or last good); never throws. */
export async function fetchFeatured(fetcher = window.fetch.bind(window)) {
  try {
    const { ok, body } = await getJson(`${API_BASE}/featured`, fetcher);
    if (!ok || !body || !Array.isArray(body.items)) throw new Error("bad featured response");
    setFeatured({ ...body, fallback: body.fallback === true, stale: body.stale === true });
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[events] featured refresh failed; keeping the last good copy", String(err));
  }
  return lastGoodFeatured;
}

/**
 * The share view's lookup. Resolves to {kind:"event", event} | {kind:"redirect", slug} | {kind:"none"}.
 * Withdrawn, draft, unknown, invalid or unreachable → "none": the normal landing, no error (§4.3).
 */
export async function fetchEvent(slug, fetcher = window.fetch.bind(window), now = Date.now()) {
  if (!isSlug(slug)) return { kind: "none" };
  const cached = slugCache.get(slug);
  if (cached && now - cached.at < REFRESH_MS) return cached.result;
  try {
    const { status, ok, body } = await getJson(`${API_BASE}/event/${encodeURIComponent(slug)}`, fetcher);
    let result = { kind: "none" };
    if (ok && body) {
      if (typeof body.redirect_to === "string" && isSlug(body.redirect_to) && body.redirect_to !== slug) result = { kind: "redirect", slug: body.redirect_to };
      else if (body.gone === true || body.visibility === "withdrawn") result = { kind: "none" };
      else if (body.slug && Array.isArray(body.event_sessions) && (body.visibility === "public" || body.visibility === "unlisted")) result = { kind: "event", event: body };
    } else if (status !== 404) throw new Error(`event lookup ${status}`);
    slugCache.set(slug, { at: now, result });
    return result;
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[events] event lookup failed", String(err));
    return cached ? cached.result : { kind: "none" };
  }
}

/** The featured list, refreshed on mount and every 5 minutes while mounted; starts from the snapshot. */
export function useFeatured() {
  const [list, setList] = useState(lastGoodFeatured);
  useEffect(() => {
    listeners.add(setList);
    fetchFeatured();
    const t = setInterval(() => { if (typeof document === "undefined" || !document.hidden) fetchFeatured(); }, REFRESH_MS);
    return () => { listeners.delete(setList); clearInterval(t); };
  }, []);
  return list;
}

// ---- the whole published window, for the browse list -------------------------------------------------------
const catalogSnapshot = () => {
  if (typeof window === "undefined") return null;
  const s = window.__EVENTS_CATALOG__;
  return s && Array.isArray(s.items) ? s : null;
};
let lastGoodCatalog = catalogSnapshot();

/** Refreshes the full catalogue. Resolves to the current best copy; never throws. */
export async function fetchCatalog(fetcher = window.fetch.bind(window)) {
  try {
    const { ok, body } = await getJson(`${API_BASE}/catalog`, fetcher);
    if (!ok || !body || !Array.isArray(body.items)) throw new Error("bad catalogue response");
    lastGoodCatalog = { ...body, stale: body.stale === true };
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[events] catalogue refresh failed; keeping the last good copy", String(err));
    // Cold start + an unreachable catalogue: with no last-good copy and no baked snapshot the list used to
    // fall through to "no dates on the calendar", which reads an outage as an empty diary (review 2026-09-16).
    if (!lastGoodCatalog) return { items: [], unreachable: true };
  }
  return lastGoodCatalog;
}

/** The full catalogue for the browse list, on the same 5-minute timer as the hero. */
export function useCatalog() {
  const [list, setList] = useState(lastGoodCatalog);
  useEffect(() => {
    let alive = true;
    const load = () => fetchCatalog().then((c) => { if (alive) setList(c); });
    load();
    const t = setInterval(() => { if (typeof document === "undefined" || !document.hidden) load(); }, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return list;
}

/** The shared event for ?e=; {kind:"pending"} until known. */
export function useSharedEvent(slug) {
  const [state, setState] = useState(() => (isSlug(slug) ? { kind: "pending" } : { kind: "none" }));
  useEffect(() => {
    let alive = true;
    if (!isSlug(slug)) { setState({ kind: "none" }); return undefined; }
    setState({ kind: "pending" });
    fetchEvent(slug).then((r) => { if (alive) setState(r); });
    return () => { alive = false; };
  }, [slug]);
  return state;
}

/** Test hook. */
export function _resetCatalog(snapshot = null) {
  lastGoodFeatured = snapshot ?? snapshotFromWindow();
  lastGoodCatalog = catalogSnapshot();
  slugCache.clear();
}
